import React from 'react';
import {
  CurrencyDollarIcon,
  EyeIcon,
  IdentificationIcon,
  InboxIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { BodyTextTW, BoldBodyTextTW } from '../../../../../../tailwindComponents/bodyText/BodyTextTW';
import { Show } from '../../../../../shared/Show';
import { eSigningMethod } from '../../../../../../types/signingAppointment/eSigningMethod';
import { getCurrencyString } from '../../../../../../ui-toolkit/utilities/getCurrencyString';
import { WitnessTooltip } from '../../../Reusable/WitnessTooltip';
import { getSigningAppointmentDisplayLanguage } from '../../../../../../types/signingAppointment/ISigningAppointmentResponse';
import { useSelectAppointmentFromPathParam } from '../../../../../../utilities/order/scheduling/useSomeAppointmentFromParam';
import {
  useListMethodsQuery,
  useListTypesQuery,
} from '../../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { ErrorBoundaryTW } from '../../../../../../tailwindComponents/errorBoundary/ErrorBoundaryTW';
import { showRate } from '../../../utilities/showRate';
import { CorrectionsButton } from 'components/order/scheduling/corrections/button/CorrectionsButton';
import { UUID } from 'types/shared/UUID';

const EMPTY_RATE = 0;

/*QUICK SIGNING INFO / ICON SECTION*/
export const TopLevelSummary: React.FC<{
  iconClassNames: string;
  onAddEventClick: (appointmentId?: UUID) => void;
}> = ({ iconClassNames, onAddEventClick }) => {
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();

  const { signingTypeLabel = '' } = useListTypesQuery(undefined, {
    selectFromResult: ({ data = [] }) => {
      return {
        signingTypeLabel: data.find((typeResponse) => typeResponse.id === appointmentFromPath?.signingType)?.label,
      };
    },
  });

  const { signingMethodLabel = '' } = useListMethodsQuery(undefined, {
    selectFromResult: ({ data = [] }) => ({
      signingMethodLabel: data.find((methodResponse) => methodResponse.id === appointmentFromPath?.signingMethod)
        ?.label,
    }),
  });

  if (!appointmentFromPath) return null;

  const onCreateCorrectionClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    onAddEventClick(appointmentFromPath.id);
  };

  const languageLabel = getSigningAppointmentDisplayLanguage(appointmentFromPath);

  return (
    <ErrorBoundaryTW>
      <section className={'flex flex-row justify-around p-2'} data-test-id={'appointment-details-tab-summary'}>
        <div>
          <div className={'flex justify-start'}>
            <InboxIcon className={iconClassNames} />
            <BoldBodyTextTW>Signing Method</BoldBodyTextTW>
          </div>
          {signingMethodLabel && <BodyTextTW className={'ml-6'}>{signingMethodLabel}</BodyTextTW>}
        </div>

        <div>
          <div className={'flex justify-start'}>
            <PencilSquareIcon className={iconClassNames} />
            <BoldBodyTextTW>Signing Type</BoldBodyTextTW>
          </div>
          {signingTypeLabel && <BodyTextTW className={'ml-6'}>{signingTypeLabel}</BodyTextTW>}
        </div>

        <Show when={showRate(appointmentFromPath.signingMethod, appointmentFromPath.isSignatureSyncAppointment)}>
          <div>
            <div className={'flex justify-start'}>
              <CurrencyDollarIcon className={iconClassNames} />
              <BoldBodyTextTW>Rate</BoldBodyTextTW>
            </div>
            <BodyTextTW className={'ml-6'}>
              {getCurrencyString(appointmentFromPath.rateCents ?? EMPTY_RATE, { fromCents: true })}
            </BodyTextTW>
          </div>
        </Show>

        <Show
          when={
            appointmentFromPath.signingMethod !== eSigningMethod.thirdPartyNotary &&
            appointmentFromPath.signingMethod !== eSigningMethod.wetSign
          }
        >
          <div>
            <div className={'flex justify-start'}>
              <IdentificationIcon className={iconClassNames} />
              <BoldBodyTextTW>Language</BoldBodyTextTW>
            </div>
            <Show when={!!languageLabel}>
              <BodyTextTW className={'ml-6'}>{languageLabel}</BodyTextTW>
            </Show>
          </div>
        </Show>
        <Show
          when={
            appointmentFromPath.signingMethod !== eSigningMethod.wetSign &&
            appointmentFromPath.signingMethod !== eSigningMethod.eSign
          }
        >
          <WitnessTooltip>
            <div className={'flex justify-start'}>
              <EyeIcon className={iconClassNames} />
              <BoldBodyTextTW>Witness</BoldBodyTextTW>
            </div>
            <BodyTextTW className={'ml-6'}>
              {appointmentFromPath.witness ? appointmentFromPath.witness : 'N/A'}
            </BodyTextTW>
          </WitnessTooltip>
        </Show>
      </section>
      <CorrectionsButton onCorrectionsButtonClick={onCreateCorrectionClick} appointment={appointmentFromPath} />
    </ErrorBoundaryTW>
  );
};
